import { RefObject, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";

const SCROLL_CONTAINER_SELECTOR = '[data-test="step-content"]';

const getRateCardSelector = (rateIndex: number): string => `[data-index="${rateIndex}"]`;

export interface RateLike {
  name?: string;
}

export interface UseRateQuickSearchParams {
  rates: RateLike[];
  containerRef: RefObject<HTMLElement | null>;
}

export interface UseRateQuickSearchResult {
  query: string;
  setQuery: (next: string) => void;
  matchIndices: number[];
  matchCount: number;
  currentMatchIndex: number;
  currentMatchRateIndex: number;
  goToPrevMatch: () => void;
  goToNextMatch: () => void;
}

export const useRateQuickSearch = ({ rates, containerRef }: UseRateQuickSearchParams): UseRateQuickSearchResult => {
  const [query, setQueryRaw] = useState("");
  const [activeMatchOrdinal, setActiveMatchOrdinal] = useState(0);
  const savedScrollTopRef = useRef<number | null>(null);

  const namesSignature = rates.map((rate) => rate.name ?? "").join("\u0001");
  const deferredQuery = useDeferredValue(query);
  const deferredNamesSignature = useDeferredValue(namesSignature);
  const matchIndices = useMemo(
    () => computeMatchIndicesFromNames(deferredNamesSignature, deferredQuery),
    [deferredNamesSignature, deferredQuery]
  );
  const matchCount = matchIndices.length;

  const currentMatchIndex = matchCount === 0 ? -1 : clampOrdinal(activeMatchOrdinal, matchCount);
  const currentMatchRateIndex = matchCount === 0 ? -1 : matchIndices[currentMatchIndex];

  const setQuery = useCallback(
    (next: string) => {
      const previousQuery = query;
      const scrollContainer = findScrollContainer(containerRef.current);

      if (!previousQuery && next && scrollContainer) {
        savedScrollTopRef.current = scrollContainer.scrollTop;
      }

      if (previousQuery && !next && scrollContainer && savedScrollTopRef.current !== null) {
        const restoreTo = savedScrollTopRef.current;
        savedScrollTopRef.current = null;
        requestAnimationFrame(() => {
          scrollContainer.scrollTo({ top: restoreTo, behavior: "smooth" });
        });
      }

      setQueryRaw(next);
      setActiveMatchOrdinal(0);
    },
    [containerRef, query]
  );

  useEffect(() => {
    if (matchCount === 0) {
      return;
    }
    scrollToRateIndex(matchIndices[clampOrdinal(activeMatchOrdinal, matchCount)]);
  }, [activeMatchOrdinal, matchCount, matchIndices]);

  const goToPrevMatch = useCallback(() => {
    if (matchCount === 0) {
      return;
    }
    setActiveMatchOrdinal((current) => {
      const next = clampOrdinal(current, matchCount) - 1;
      return next < 0 ? matchCount - 1 : next;
    });
  }, [matchCount]);

  const goToNextMatch = useCallback(() => {
    if (matchCount === 0) {
      return;
    }
    setActiveMatchOrdinal((current) => {
      const next = clampOrdinal(current, matchCount) + 1;
      return next >= matchCount ? 0 : next;
    });
  }, [matchCount]);

  return {
    query,
    setQuery,
    matchIndices,
    matchCount,
    currentMatchIndex,
    currentMatchRateIndex,
    goToPrevMatch,
    goToNextMatch,
  };
};

const NAMES_SEPARATOR = "\u0001";

const computeMatchIndicesFromNames = (namesSignature: string, query: string): number[] => {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) {
    return [];
  }
  const indices: number[] = [];
  const names = namesSignature.split(NAMES_SEPARATOR);
  for (let index = 0; index < names.length; index += 1) {
    if (names[index].toLowerCase().includes(trimmed)) {
      indices.push(index);
    }
  }
  return indices;
};

const clampOrdinal = (ordinal: number, matchCount: number): number => {
  if (matchCount === 0) {
    return 0;
  }
  if (ordinal < 0) {
    return 0;
  }
  if (ordinal >= matchCount) {
    return matchCount - 1;
  }
  return ordinal;
};

const findScrollContainer = (start: HTMLElement | null): HTMLElement | null => {
  let current: HTMLElement | null = start;
  while (current) {
    if (current.matches?.(SCROLL_CONTAINER_SELECTOR)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const scrollToRateIndex = (rateIndex: number): void => {
  if (typeof document === "undefined") {
    return;
  }
  requestAnimationFrame(() => {
    const element = document.querySelector<HTMLElement>(getRateCardSelector(rateIndex));
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
};
